import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { PlaceholderBar } from '../../components/loading-skeleton/index';
import {
  CapTableGraphPlaceholder,
  CapTableOverAllStatusPlaceholder,
} from '../cap-table/components/cap-table-loading-placeholders';

type DashboardPlaceholderProps = {
  isDesktop: boolean;
  isTablet: boolean;
};

const SectionShell: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={twMerge(
      'flex min-h-full flex-col rounded-lg bg-gray-50 p-1 max-lg:w-full',
      className,
    )}
  >
    <div className="flex w-full items-start justify-between gap-4 px-4 py-3">
      <PlaceholderBar className="h-3 w-32 rounded bg-gray-100" />
      <PlaceholderBar className="size-4 rounded bg-gray-100" />
    </div>
    {children}
  </div>
);

const Top5Placeholder: FC = () => (
  <div className="flex h-full w-full min-w-[400px] flex-col gap-2 rounded-md bg-white p-2 shadow-sm lg:px-6">
    <div className="flex min-h-[34px] w-full justify-between gap-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" key={index} />
      ))}
    </div>
    {Array.from({ length: 5 }).map((_, index) => (
      <div className="flex h-[46px] w-full items-center justify-between gap-3" key={index}>
        <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
        <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
        <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
        <PlaceholderBar className="h-4 w-10 rounded bg-gray-100" />
      </div>
    ))}
  </div>
);

const PoolAllocationPlaceholder: FC<{ isDesktop: boolean; isTablet: boolean }> = ({
  isDesktop,
  isTablet,
}) => {
  const isMobile = !isDesktop && !isTablet;

  return (
    <div
      className={twMerge(
        'relative flex h-[280px] w-full items-center justify-center gap-9 rounded-md bg-white shadow-sm',
        isDesktop ? 'p-6' : isTablet ? 'p-5' : 'h-full flex-col gap-6 p-2',
      )}
    >
      <PlaceholderBar
        className={twMerge('rounded-full bg-gray-100', isTablet ? 'size-[130px]' : 'size-[214px]')}
      />
      <div className={twMerge('flex flex-col gap-6', isMobile && 'flex-row gap-4')}>
        {Array.from({ length: 2 }).map((_, index) => (
          <div className="flex w-[108px] flex-col gap-2" key={index}>
            <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
            <PlaceholderBar className="h-3 w-12 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const DashboardPlaceholder: FC<DashboardPlaceholderProps> = ({ isDesktop, isTablet }) => (
  <div className="flex h-fit w-full animate-pulse flex-col gap-4 overflow-hidden lg:pl-1">
    <div className="flex h-max w-full items-stretch gap-4 max-lg:flex-col">
      <CapTableOverAllStatusPlaceholder wrapperClassName={isTablet ? 'w-[40%]' : 'w-[100%]'} />
      <CapTableGraphPlaceholder
        isDesktop={isDesktop}
        isTablet={isTablet}
        wrapperClassName={isTablet ? 'w-[60%]' : 'w-[100%]'}
      />
    </div>
    <div className="flex h-max w-full justify-center gap-4 max-lg:flex-col">
      <SectionShell className={isDesktop || isTablet ? 'w-[50%]' : 'w-[100%]'}>
        <Top5Placeholder />
      </SectionShell>
      <SectionShell className={isDesktop || isTablet ? 'w-[50%]' : 'w-[100%]'}>
        <PoolAllocationPlaceholder isDesktop={isDesktop} isTablet={isTablet} />
      </SectionShell>
    </div>
    <div className="h-max w-full max-lg:hidden">
      <SectionShell>
        <div className="flex h-full w-full rounded-md bg-white p-6 shadow-sm">
          <PlaceholderBar className="h-[344px] w-full rounded-md bg-gray-100" />
        </div>
      </SectionShell>
    </div>
  </div>
);
